import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTheme } from '../../context/ThemeContext';
import SolidMapPin from '../../components/SolidMapPin';
import TakeoffArrow from '../../components/TakeoffArrow';
import LandingArrow from '../../components/LandingArrow';
import { ChevronDown, ChevronUp } from 'lucide-react';

const LegSummary = ({ legIndex, leg, onEdit, isExpanded = false }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [expanded, setExpanded] = useState(isExpanded);

  // Get leg data from either prop or formData
  if (!leg) return null;

  const containerClassName = clsx(
    'rounded-lg border transition-all duration-200',
    {
      'bg-slate-800 border-slate-700': isDark,
      'bg-gray-100 border-gray-300': !isDark
    }
  );

  const headerClassName = clsx(
    'flex items-center justify-between p-4 cursor-pointer',
    {
      'hover:bg-slate-700': isDark,
      'hover:bg-gray-200': !isDark
    }
  );

  const titleClassName = clsx(
    'text-sm font-medium flex items-center gap-2',
    {
      'text-slate-200': isDark,
      'text-gray-900': !isDark
    }
  );

  const sectionClassName = clsx(
    'bg-white px-4 py-2 pb-4 space-y-4',
    {
      'border-t border-slate-600': isDark && expanded,
      'border-t border-gray-200': !isDark && expanded
    }
  );

  const sectionHeaderClassName = clsx(
    'text-sm font-semibold flex items-center gap-2',
    {
      'text-slate-300': isDark,
      'text-gray-700': !isDark
    }
  );

  const addressClassName = clsx(
    'text-sm',
    {
      'text-slate-400': isDark,
      'text-gray-600': !isDark
    }
  );

  const iconClassName = clsx(
    'w-4 h-4',
    {
      'text-slate-400': isDark,
      'text-gray-500': !isDark
    }
  );

  const editButtonClassName = clsx(
    'text-xs underline hover:no-underline mr-2',
    {
      'text-blue-400 hover:text-blue-300': isDark,
      'text-blue-600 hover:text-blue-700': !isDark
    }
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatAddress = (address) => {
    if (!address) return 'Not specified';
    if (typeof address === 'string') return address;
    
    const parts = [];
    
    // Extract name/recipient if available
    const recipientName = address.recipientName || address.senderName;
    if (recipientName) {
      parts.push(recipientName);
    }
    
    // Extract street address
    const streetAddress = address.name || address.formatted_address || address.place?.formattedAddress;
    if (streetAddress) {
      parts.push(streetAddress);
    }
    
    // Extract city, state, zip
    const cityName = address.city || address.name || address.place?.displayName;
    const stateName = address.state || address.place?.addressComponents?.find(
      comp => comp.types?.includes('administrative_area_level_1')
    )?.shortText;
    const zipCode = address.zip || address.place?.addressComponents?.find(
      comp => comp.types?.includes('postal_code')
    )?.longText;
    const countryName = address.country || address.place?.addressComponents?.find(
      comp => comp.types?.includes('country')
    )?.longText;
    
    if (cityName || stateName || zipCode) {
      const locationParts = [];
      if (cityName) locationParts.push(cityName);
      if (stateName) locationParts.push(stateName);
      if (zipCode) locationParts.push(zipCode);
      parts.push(locationParts.join(', '));
    }
    
    if (countryName) {
      parts.push(countryName);
    }
    
    return parts.length > 0 ? parts : ['Not specified'];
  };

  const formatCruiseAddress = (address) => {
    if (!address) return 'Not specified';
    if (typeof address === 'string') return address;
    
    const parts = [];
    
    // Extract name/recipient if available
    const recipientName = address.recipientName || address.senderName;
    if (recipientName) {
      parts.push(recipientName);
    }
    
    // Extract street address
    const portName = address.cruisePort?.name;
    if (portName) {
      parts.push(portName);
    }
    const cruiseLineName = address.cruiseLine?.name;
    if (cruiseLineName) {
      parts.push(cruiseLineName);
    }

    const cruiseFilter = address.cruiseFilter;
    if (cruiseFilter && address.selectedItinerary?.[cruiseFilter]) {
      parts.push(address.selectedItinerary[cruiseFilter]);
    }
    if (address.selectedItinerary) {
      const itin = address.selectedItinerary;
      parts.push(
        [
          `${itin.embarkation_date} - ${itin.disembarkation_date} | `,
          `${itin.embarkation_port} to ${itin.disembarkation_port}`
        ].join(' ')
      );
    }
    if(address?.city) {
      parts.push(
        `${address.city}, ${address.state} ${address.country}`.trim()
      )
    }
    return parts.length > 0 ? parts : ['Not specified'];
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    // For pickup times, you might have specific time windows
    // This is a placeholder - adjust based on your actual time data
    return 'Pickup between 4-5pm';
  };

  const formatRoutePreview = (fromAddress, toAddress) => {
    const formatShort = (address) => {
      if (!address) return 'Unknown';
      if (typeof address === 'string') return address;
      
      const cityName = address.city || address.name || address.place?.displayName;
      const stateName = address.state || address.place?.addressComponents?.find(
        comp => comp.types?.includes('administrative_area_level_1')
      )?.shortText;
      
      if (cityName && stateName) {
        return `${cityName}, ${stateName}`;
      }
      return cityName || stateName || address.cruisePort?.city || 'Unknown';
    };
    
    return `${formatShort(fromAddress)} → ${formatShort(toAddress)}`;
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const fromAddressParts = leg.fromAddress?.cruise ? 
    formatCruiseAddress(leg.fromAddress) : formatAddress(leg.fromAddress);
  const toAddressParts = leg.toAddress?.cruise ? 
    formatCruiseAddress(leg.toAddress) : formatAddress(leg.toAddress);
  const currentLegIndex = legIndex !== undefined ? legIndex : (leg.legIndex || 0);

  return (
    <div className={containerClassName}>
      <div className={headerClassName} onClick={toggleExpanded}>
        <div className="flex items-center gap-3">
          {currentLegIndex == 0 && <TakeoffArrow className={iconClassName} />}
          {currentLegIndex > 0 && <LandingArrow className={iconClassName} />}
          <div>
            <h3 className={titleClassName}>
              Leg {currentLegIndex + 1}
            </h3>
            <div className={clsx(
              'text-xs mt-1',
              {
                'text-slate-400': isDark,
                'text-gray-500': !isDark
              }
            )}>
              {formatRoutePreview(leg.fromAddress, leg.toAddress)}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(currentLegIndex);
              }}
              className={editButtonClassName}
            >
              Edit
            </button>
          )}
          {expanded ? (
            <ChevronUp className={iconClassName} />
          ) : (
            <ChevronDown className={iconClassName} />
          )}
        </div>
      </div>

      {expanded && (
        <div className={sectionClassName}>
          {/* Origin and Destination - responsive grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Origin */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <SolidMapPin className={iconClassName} />
                <h4 className={sectionHeaderClassName}>Origin</h4>
              </div>
              <div className={addressClassName}>
                {fromAddressParts.map((part, index) => (
                  <div key={index}>{part}</div>
                ))}
              </div>
            </div>

            {/* Destination */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <SolidMapPin className={iconClassName} />
                <h4 className={sectionHeaderClassName}>Destination</h4>
              </div>
              <div className={addressClassName}>
                {toAddressParts.map((part, index) => (
                  <div key={index}>{part}</div>
                ))}
              </div>
            </div>
          </div>

          {/* Pickup and Delivery - responsive grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Pickup */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <TakeoffArrow className={iconClassName} />
                <h4 className={sectionHeaderClassName}>Pickup</h4>
              </div>
              <div className={addressClassName}>
                <div>{formatDate(leg.shipDate)}</div>
                {leg.pickupDate && (
                  <div className="text-xs">{formatTime(leg.pickupDate)}</div>
                )}
              </div>
            </div>

            {/* Delivery */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <LandingArrow className={iconClassName} />
                <h4 className={sectionHeaderClassName}>Delivery</h4>
              </div>
              <div className={addressClassName}>
                <div>{formatDate(leg.deliveryDate)}</div>
                {leg?.selectedService?.display_name && leg?.cruiseSelection == 'delivery' && (
                  <div className="text-xs">
                    Guaranteed by cruise embark time
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

LegSummary.propTypes = {
  legIndex: PropTypes.number,
  leg: PropTypes.shape({
    legIndex: PropTypes.number,
    fromAddress: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    toAddress: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    shipDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date)
    ]),
    deliveryDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date)
    ]),
    pickupDate: PropTypes.string,
    selectedService: PropTypes.shape({
      display_name: PropTypes.string
    }),
    selectedProducts: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      display_name: PropTypes.string,
      price_cents: PropTypes.number,
      quantity: PropTypes.number
    }))
  }),
  onEdit: PropTypes.func,
  isExpanded: PropTypes.bool
};

export default LegSummary;